import { useSyncExternalStore } from 'react';

/**
 * Authoritative tenant session context.
 *
 * The tenant bootstrap resolves and authorizes a tenant for the authenticated
 * session. That result is stored here and attached to EVERY subsequent API
 * request as an `x-tenant-slug` header, so server services resolve the exact same
 * tenant for the lifetime of the session instead of re-deriving the tenant from
 * hostname/x-forwarded-host headers (which can differ per request and cause
 * intermittent tenant_access_denied during workspace loading).
 *
 * Security: the header is only ever read AFTER the server verifies the bearer
 * token, and every consuming service still runs its own tenant allowlist /
 * membership authorization. This header only pins which tenant to resolve.
 */

let authoritativeTenantSlug: string | null = null;
const tenantContextListeners = new Set<() => void>();

export function setAuthoritativeTenantSlug(slug: string | null | undefined): void {
  const next = slug ? String(slug).trim().toLowerCase() || null : null;
  if (next === authoritativeTenantSlug) return;
  authoritativeTenantSlug = next;
  tenantContextListeners.forEach((listener) => listener());
  try {
    window.dispatchEvent(new CustomEvent('trade-proof-tenant-context-changed', { detail: { tenantSlug: next } }));
  } catch {}
}

export function getAuthoritativeTenantSlug(): string | null {
  return authoritativeTenantSlug;
}

export function clearAuthoritativeTenantSlug(): void {
  setAuthoritativeTenantSlug(null);
}

export function useAuthoritativeTenantSlug(): string | null {
  return useSyncExternalStore(
    (listener) => {
      tenantContextListeners.add(listener);
      return () => tenantContextListeners.delete(listener);
    },
    getAuthoritativeTenantSlug,
    () => null,
  );
}

function readCurrentHostname(): string {
  try {
    return String(window.location.hostname || '').trim();
  } catch {
    return '';
  }
}

/**
 * Applies the authoritative tenant context to a request header bag:
 *   - `x-tenant-slug`: the tenant the bootstrap authorized (authoritative)
 *   - `x-forwarded-host`: the browser hostname (legacy/fallback signal)
 * Returns the same headers object so callers can chain.
 */
export function applyTenantRequestHeaders(headers: Record<string, string>): Record<string, string> {
  const slug = getAuthoritativeTenantSlug();
  if (slug) headers['x-tenant-slug'] = slug;
  const hostname = readCurrentHostname();
  if (hostname) headers['x-forwarded-host'] = hostname;
  return headers;
}
